import { formatDateInIST, formatPeople } from './reportExport';
import { detailedStatusCategory } from './trackerNormalization';

const MS_PER_DAY = 86400000;

// Day index in the IST calendar, so "overdue" flips at IST midnight rather than
// at the browser's local midnight.
const istDayNumber = (value) => {
  const iso = formatDateInIST(value);
  if (!iso) return null;
  const time = Date.parse(`${iso}T00:00:00Z`);
  return Number.isNaN(time) ? null : Math.floor(time / MS_PER_DAY);
};

export const DUE_BUCKETS = [
  { id: 'overdue', label: 'Overdue', tone: 'error' },
  { id: 'next7', label: 'Due in 7 days', tone: 'warning' },
  { id: 'next30', label: 'Due in 30 days', tone: 'info' },
  { id: 'later', label: 'Later', tone: 'success' },
  { id: 'none', label: 'No date', tone: 'default' },
];

const DUE_BUCKET_BY_ID = new Map(DUE_BUCKETS.map((bucket) => [bucket.id, bucket]));

export const dueBucketFor = (value, now = new Date()) => {
  const day = istDayNumber(value);
  const today = istDayNumber(now);
  if (day === null || today === null) {
    return { id: 'none', days: null, chipLabel: '-', tone: 'default' };
  }
  const days = day - today;
  if (days < 0) {
    return { id: 'overdue', days, chipLabel: `Overdue ${Math.abs(days)}d`, tone: 'error' };
  }
  if (days === 0) return { id: 'next7', days, chipLabel: 'Today', tone: 'error' };
  if (days <= 7) return { id: 'next7', days, chipLabel: `In ${days}d`, tone: 'warning' };
  if (days <= 30) return { id: 'next30', days, chipLabel: `In ${days}d`, tone: 'info' };
  return { id: 'later', days, chipLabel: `In ${days}d`, tone: 'success' };
};

export const dueBucketLabel = (id) => DUE_BUCKET_BY_ID.get(id)?.label || id;

export const dueDateKeyFor = (mode) =>
  (mode === 'trackerDueDate' ? 'trackerDueDate' : 'complianceDeadline');

export const CRITICALITY_TONES = {
  High: 'error',
  Medium: 'warning',
  Low: 'success',
};

const splitList = (value) =>
  String(value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

export const lawAreaValuesOf = (row) => splitList(row?.lawArea);
export const departmentValuesOf = (row) => splitList(row?.departments);

const textOf = (value) => String(value ?? '').trim();
const lower = (value) => textOf(value).toLowerCase();
const dateSortValue = (value) => {
  const time = value ? new Date(value).getTime() : Number.NaN;
  // Missing dates sort last in both directions.
  return Number.isFinite(time) ? time : null;
};

const complianceColumns = [
  {
    id: 'titleOrActivity',
    label: 'Legal Update Title',
    type: 'legalUpdateLink',
    minWidth: 320,
    sticky: true,
    defaultVisible: true,
  },
  { id: 'updateNumber', label: 'Update No.', type: 'text', minWidth: 150, defaultVisible: true },
  { id: 'lawArea', label: 'Law Area', type: 'text', minWidth: 150, defaultVisible: true },
  { id: 'departments', label: 'Departments', type: 'text', minWidth: 160, defaultVisible: true },
  {
    id: 'criticalityLevel',
    label: 'Criticality',
    type: 'criticality',
    minWidth: 120,
    defaultVisible: true,
  },
  { id: 'issueDate', label: 'Issue Date', type: 'date', minWidth: 130, defaultVisible: true },
  {
    id: 'complianceDeadline',
    label: 'Last date to comply',
    type: 'dueDate',
    minWidth: 180,
    defaultVisible: true,
  },
];

const trackerColumns = [
  {
    id: 'titleOrActivity',
    label: 'Compliance Activity',
    type: 'trackerLink',
    minWidth: 320,
    sticky: true,
    defaultVisible: true,
  },
  { id: 'uin', label: 'UIN', type: 'trackerLink', minWidth: 150, defaultVisible: true },
  { id: 'status', label: 'Status', type: 'status', minWidth: 170, defaultVisible: true },
  {
    id: 'trackerDueDate',
    label: 'Task due date',
    type: 'dueDate',
    minWidth: 165,
    defaultVisible: true,
  },
  { id: 'trackerOwner', label: 'Owners', type: 'people', minWidth: 200, defaultVisible: true },
  { id: 'trackerManager', label: 'Managers', type: 'people', minWidth: 200, defaultVisible: false },
  { id: 'lawArea', label: 'Law Area', type: 'text', minWidth: 150, defaultVisible: true },
  {
    id: 'criticalityLevel',
    label: 'Criticality',
    type: 'criticality',
    minWidth: 120,
    defaultVisible: true,
  },
  { id: 'location', label: 'Location', type: 'text', minWidth: 140, defaultVisible: false },
  { id: 'risk', label: 'Risk', type: 'text', minWidth: 110, defaultVisible: false },
  { id: 'departments', label: 'Departments', type: 'text', minWidth: 160, defaultVisible: false },
  {
    id: 'legalUpdateTitle',
    label: 'Legal Update',
    type: 'legalUpdateLink',
    minWidth: 280,
    defaultVisible: false,
  },
  { id: 'updateNumber', label: 'Update No.', type: 'text', minWidth: 150, defaultVisible: false },
  { id: 'issueDate', label: 'Issue Date', type: 'date', minWidth: 130, defaultVisible: false },
  {
    id: 'complianceDeadline',
    label: 'Last date to comply',
    type: 'date',
    minWidth: 180,
    defaultVisible: false,
  },
];

// Plain text for a cell, reused by search, grouping, TSV copy and the tooltip.
export const columnText = (column, row) => {
  switch (column.type) {
    case 'people':
      return formatPeople(row[column.id]);
    case 'status':
      return row[column.id] ? detailedStatusCategory(row[column.id]) : '';
    case 'date':
    case 'dueDate':
      return formatDateInIST(row[column.id]);
    default:
      return textOf(row[column.id]);
  }
};

const columnSortValue = (column, row) => {
  if (column.type === 'date' || column.type === 'dueDate') {
    return dateSortValue(row[column.id]);
  }
  const value = columnText(column, row);
  return value ? value.toLowerCase() : null;
};

export const columnsForMode = (mode) =>
  (mode === 'trackerDueDate' ? trackerColumns : complianceColumns);

export const defaultVisibleColumns = (mode) =>
  columnsForMode(mode)
    .filter((column) => column.defaultVisible)
    .map((column) => column.id);

export const groupOptionsForMode = (mode) => {
  const options = [
    { id: 'none', label: 'No grouping' },
    { id: 'dueBucket', label: 'Due status' },
    { id: 'lawArea', label: 'Law area' },
    { id: 'criticalityLevel', label: 'Criticality' },
    { id: 'departments', label: 'Department' },
  ];
  if (mode === 'trackerDueDate') options.push({ id: 'status', label: 'Status' });
  return options;
};

const countInto = (map, key) => map.set(key, (map.get(key) || 0) + 1);

const sortedCounts = (map) =>
  Array.from(map.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));

// Facet option lists and tile counts are derived from the full loaded set so the
// numbers stay meaningful while the user narrows the view.
export const buildFacetIndex = (rows, mode, now = new Date()) => {
  const dueKey = dueDateKeyFor(mode);
  const lawAreas = new Map();
  const criticalities = new Map();
  const statuses = new Map();
  const departments = new Map();
  const dueBuckets = new Map(DUE_BUCKETS.map((bucket) => [bucket.id, 0]));

  rows.forEach((row) => {
    const areas = lawAreaValuesOf(row);
    (areas.length ? areas : ['Unspecified']).forEach((area) => countInto(lawAreas, area));

    const departmentValues = departmentValuesOf(row);
    (departmentValues.length ? departmentValues : ['Unspecified']).forEach((department) =>
      countInto(departments, department)
    );

    countInto(criticalities, textOf(row.criticalityLevel) || 'Unspecified');
    if (mode === 'trackerDueDate') {
      countInto(statuses, row.status ? detailedStatusCategory(row.status) : 'Unspecified');
    }

    const bucket = dueBucketFor(row[dueKey], now);
    dueBuckets.set(bucket.id, (dueBuckets.get(bucket.id) || 0) + 1);
  });

  return {
    lawAreas: sortedCounts(lawAreas),
    criticalities: sortedCounts(criticalities),
    statuses: sortedCounts(statuses),
    departments: sortedCounts(departments),
    dueBuckets: DUE_BUCKETS.map((bucket) => ({
      ...bucket,
      count: dueBuckets.get(bucket.id) || 0,
    })),
    total: rows.length,
  };
};

export const EMPTY_FACETS = {
  lawAreas: [],
  criticalities: [],
  statuses: [],
  departments: [],
  dueBuckets: [],
};

export const hasActiveFacets = (facets, search) =>
  Boolean(textOf(search))
  || Object.values(facets || {}).some((values) => Array.isArray(values) && values.length);

const matchesAny = (selected, values) =>
  !selected.length || values.some((value) => selected.includes(value));

export const applySearch = (rows, search, mode) => {
  const needle = lower(search);
  if (!needle) return rows;
  const columns = columnsForMode(mode);
  const terms = needle.split(/\s+/).filter(Boolean);
  return rows.filter((row) => {
    const haystack = columns.map((column) => columnText(column, row)).join(' ').toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
};

export const applyFacets = (rows, facets, mode, now = new Date()) => {
  const selected = { ...EMPTY_FACETS, ...(facets || {}) };
  const dueKey = dueDateKeyFor(mode);
  const active =
    selected.lawAreas.length
    || selected.criticalities.length
    || selected.statuses.length
    || selected.departments.length
    || selected.dueBuckets.length;
  if (!active) return rows;

  return rows.filter((row) => {
    const areas = lawAreaValuesOf(row);
    if (!matchesAny(selected.lawAreas, areas.length ? areas : ['Unspecified'])) return false;

    const departments = departmentValuesOf(row);
    if (!matchesAny(selected.departments, departments.length ? departments : ['Unspecified'])) {
      return false;
    }

    if (!matchesAny(selected.criticalities, [textOf(row.criticalityLevel) || 'Unspecified'])) {
      return false;
    }

    if (selected.statuses.length) {
      const status = row.status ? detailedStatusCategory(row.status) : 'Unspecified';
      if (!selected.statuses.includes(status)) return false;
    }

    if (selected.dueBuckets.length) {
      if (!selected.dueBuckets.includes(dueBucketFor(row[dueKey], now).id)) return false;
    }

    return true;
  });
};

export const sortRows = (rows, sort, mode) => {
  if (!sort?.columnId || !sort?.direction) return rows;
  const column = columnsForMode(mode).find((item) => item.id === sort.columnId);
  if (!column) return rows;
  const factor = sort.direction === 'desc' ? -1 : 1;

  return [...rows].sort((a, b) => {
    const aValue = columnSortValue(column, a);
    const bValue = columnSortValue(column, b);
    const aEmpty = aValue === null || aValue === '';
    const bEmpty = bValue === null || bValue === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (aValue === bValue) return 0;
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * factor;
    }
    return String(aValue).localeCompare(String(bValue)) * factor;
  });
};

const groupKeysFor = (row, groupBy, mode, now) => {
  if (groupBy === 'dueBucket') {
    return [dueBucketLabel(dueBucketFor(row[dueDateKeyFor(mode)], now).id)];
  }
  if (groupBy === 'lawArea') {
    const values = lawAreaValuesOf(row);
    return values.length ? values : ['Unspecified'];
  }
  if (groupBy === 'departments') {
    const values = departmentValuesOf(row);
    return values.length ? values : ['Unspecified'];
  }
  if (groupBy === 'status') {
    return [row.status ? detailedStatusCategory(row.status) : 'Unspecified'];
  }
  return [textOf(row[groupBy]) || 'Unspecified'];
};

// A row can belong to several groups when grouping by a multi-value field such
// as law area, which is why group counts can exceed the filtered row count.
export const groupRows = (rows, groupBy, mode, now = new Date()) => {
  if (!groupBy || groupBy === 'none') return null;
  const buckets = new Map();
  rows.forEach((row) => {
    groupKeysFor(row, groupBy, mode, now).forEach((key) => {
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(row);
    });
  });

  const groups = Array.from(buckets.entries()).map(([key, groupedRows]) => ({
    key,
    rows: groupedRows,
  }));

  if (groupBy === 'dueBucket') {
    const order = DUE_BUCKETS.map((bucket) => bucket.label);
    return groups.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }
  return groups.sort((a, b) => b.rows.length - a.rows.length || a.key.localeCompare(b.key));
};

const tsvCell = (value) => String(value ?? '').replace(/[\t\r\n]+/g, ' ').trim();

export const rowsToTsv = (rows, columns) => {
  const header = columns.map((column) => tsvCell(column.label)).join('\t');
  const body = rows.map((row) =>
    columns.map((column) => tsvCell(columnText(column, row))).join('\t')
  );
  return [header, ...body].join('\n');
};
